import { useMemo, useState } from "react";
import { motion } from "motion/react";
import { Check, Search, X } from "lucide-react";
import { cn, formatRelativeTime } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button";
import { TabEmpty, UsersPeriodSelect } from "../users-ui";
import { useUsersContext } from "../../context/UsersContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import {
  INCENTIVE_POINT_STATUS,
  INCENTIVE_POINT_STATUS_VALUES,
} from "@/other/constants/IncentiveKeys.jsx";
import {
  USERS_HELPER_CLS,
  USERS_HERO_METRIC_CLS,
  USERS_META_CLS,
  USERS_SUBTITLE_CLS,
  USERS_TITLE_CLS,
} from "../../constants/typography";

// ─── Utilities ───────────────────────────────────────────────────────────────

const ptsFormatter = new Intl.NumberFormat(undefined, { signDisplay: "exceptZero" });
function formatPts(n: number) { return ptsFormatter.format(n); }

// ─── Status meta ─────────────────────────────────────────────────────────────

const STATUS_META: Record<string, { rail: string; pts: string }> = {
  Pending:  { rail: "bg-amber-500",   pts: "text-amber-500 dark:text-amber-400" },
  Approved: { rail: "bg-success",     pts: "text-foreground" },
  Awarded:  { rail: "bg-success",     pts: "text-foreground" },
  Denied:   { rail: "bg-destructive", pts: "text-destructive" },
};
const DEFAULT_META = { rail: "bg-muted-foreground/40", pts: "text-foreground" };

// Footer total color per tab
const TAB_TOTAL_CLS: Record<string, string> = {
  awarded:  "text-success",
  pending:  "text-amber-500 dark:text-amber-400",
  approved: "text-success",
  denied:   "text-destructive",
};

// ─── Tab definitions ─────────────────────────────────────────────────────────

type StatusTabId = "awarded" | "pending" | "approved" | "denied";

const STATUS_TABS: Array<{
  id: StatusTabId;
  label: string;
  faIcon: string;
  statusRaw: number;
}> = [
  { id: "awarded",  label: "Awarded",  faIcon: "fa-light fa-trophy",       statusRaw: INCENTIVE_POINT_STATUS.AWARDED  },
  { id: "pending",  label: "Pending",  faIcon: "fa-light fa-clock",         statusRaw: INCENTIVE_POINT_STATUS.PENDING  },
  { id: "approved", label: "Approved", faIcon: "fa-light fa-circle-check",  statusRaw: INCENTIVE_POINT_STATUS.APPROVED },
  { id: "denied",   label: "Denied",   faIcon: "fa-light fa-circle-xmark",  statusRaw: INCENTIVE_POINT_STATUS.DENIED   },
];

// ─── Data ────────────────────────────────────────────────────────────────────

interface PointsEntry {
  id: string | number;
  name: string;
  entity?: string;
  date?: string;
  points: number;
  statusLabel: string;
  statusRaw: number | null;
  archived: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record: any;
}

// ─── PointRow ─────────────────────────────────────────────────────────────────

function PointRow({ entry, index }: { entry: PointsEntry; index: number }) {
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[entry.statusLabel] ?? DEFAULT_META;
  const isPending = entry.statusRaw === INCENTIVE_POINT_STATUS.PENDING;

  async function changeStatus(newStatus: number) {
    if (!entry.record) return;
    setBusy(true);
    try {
      await entry.record.set("status", newStatus);
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: index * 0.03, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "relative rounded-lg border overflow-hidden",
        "border-border/70 bg-card",
        entry.archived && "opacity-60",
      )}
    >
      {/* Left status rail */}
      <span aria-hidden className={cn("absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full", meta.rail)} />

      <div className="pl-3.5 pr-3.5 pt-3.5 pb-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className={cn(
              USERS_TITLE_CLS,
              "font-normal truncate leading-none",
              entry.archived && "line-through",
            )}>
              {entry.name}
            </p>
            {(entry.entity || entry.date) && (
              <p className={cn(USERS_SUBTITLE_CLS, "mt-1.5 truncate")}>
                {[entry.entity, entry.date].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p className={cn(USERS_TITLE_CLS, "tabular-nums leading-none", meta.pts)}>
              {formatPts(entry.points)}
            </p>
          </div>
        </div>

        {/* Inline actions for pending items */}
        {isPending && !entry.archived && (
          <div className="mt-2.5 pt-2 border-t border-border/40 flex items-center justify-end gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              className="h-7 px-2.5 gap-1 text-sm font-normal text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => changeStatus(INCENTIVE_POINT_STATUS.DENIED)}
            >
              <X className="size-3" />
              Reject
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              className="h-7 px-2.5 gap-1 text-sm font-normal text-success hover:text-success hover:bg-success/10"
              onClick={() => changeStatus(INCENTIVE_POINT_STATUS.APPROVED)}
            >
              <Check className="size-3" />
              Approve
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

type PeriodKey = "all" | "week" | "month" | "year";

const POINTS_PERIOD_OPTIONS: { value: PeriodKey; label: string }[] = [
  { value: "all",   label: "All" },
  { value: "week",  label: "This week" },
  { value: "month", label: "This month" },
  { value: "year",  label: "This year" },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function PointsTab({ user }: { user: any }) {
  const { project } = useUsersContext();
  const [period, setPeriod]     = useState<PeriodKey>("all");
  const [active, setActive]     = useState<StatusTabId>("awarded");
  const [query, setQuery]       = useState("");

  const { version: pointsVersion } = useSwivuStore({ storeId: STORE_KEYS.incentivePointsStore });
  const { version: usersVersion }  = useSwivuStore({ storeId: STORE_KEYS.boardUserStore });

  // Period window filter
  const periodStart = useMemo<Date | null>(() => {
    const now = new Date();
    if (period === "week")  { const d = new Date(now); d.setDate(d.getDate() - 7);         return d; }
    if (period === "month") { const d = new Date(now); d.setMonth(d.getMonth() - 1);        return d; }
    if (period === "year")  { const d = new Date(now); d.setFullYear(d.getFullYear() - 1);  return d; }
    return null;
  }, [period]);

  const ledger = useMemo<PointsEntry[]>(() => {
    if (!project) return [];
    const all = project.incentivePointsStore.query(
      (p) => p.boardUserId === user.id && !p.archived,
    );
    return all
      .filter((p) => {
        if (!periodStart) return true;
        const d = p.dateEarned ? new Date(p.dateEarned) : p.createdAt ? new Date(p.createdAt) : null;
        return !d || d >= periodStart;
      })
      .map((p): PointsEntry => {
        const statusRaw = (p.status ?? null) as number | null;
        const statusLabel: string =
          (INCENTIVE_POINT_STATUS_VALUES as Record<number, string>)[statusRaw as number] ??
          String(statusRaw ?? "");
        return {
          id: p.id,
          name: p.incentiveName ?? `Point #${p.id}`,
          entity: p.taskName ?? undefined,
          date: formatRelativeTime(p.dateEarned || p.createdAt) || undefined,
          points: p.points ?? 0,
          statusLabel,
          statusRaw,
          archived: p.archived ?? false,
          record: p,
        };
      });
  }, [user.id, pointsVersion, project, periodStart]);

  // ─ Board average for "vs board" delta
  const boardAvgDelta = useMemo<number | null>(() => {
    if (!project) return null;
    const allPts   = project.incentivePointsStore.query((p) => !p.archived);
    const allUsers = project.boardUsersStore.query(() => true);
    if (!allUsers.length) return null;
    const boardTotal = allPts
      .filter((p) => {
        if (!periodStart) return true;
        const d = p.dateEarned ? new Date(p.dateEarned) : p.createdAt ? new Date(p.createdAt) : null;
        return !d || d >= periodStart;
      })
      .reduce((s, p) => s + (p.points ?? 0), 0);
    const avg     = boardTotal / allUsers.length;
    const myTotal = ledger.reduce((s, e) => s + e.points, 0);
    return Math.round(myTotal - avg);
  }, [project, ledger, periodStart, pointsVersion, usersVersion]);

  if (ledger.length === 0 && !project) {
    return <TabEmpty icon="fa-light fa-award" message="No points activity for this user." />;
  }

  // KPI stats (over all non-archived, all periods)
  const totalPts   = ledger.reduce((s, e) => s + e.points, 0);
  const earnedPts  = ledger
    .filter((e) => e.statusRaw === INCENTIVE_POINT_STATUS.AWARDED || e.statusRaw === INCENTIVE_POINT_STATUS.APPROVED)
    .reduce((s, e) => s + e.points, 0);
  const pendingPts = ledger
    .filter((e) => e.statusRaw === INCENTIVE_POINT_STATUS.PENDING)
    .reduce((s, e) => s + e.points, 0);
  const settledPct = totalPts > 0 ? Math.round((earnedPts / totalPts) * 100) : 0;

  // Tab item counts (always show all 4 tabs)
  const tabCounts = useMemo(() => {
    const map: Record<StatusTabId, number> = { awarded: 0, pending: 0, approved: 0, denied: 0 };
    for (const tab of STATUS_TABS) {
      map[tab.id] = ledger.filter((e) => e.statusRaw === tab.statusRaw).length;
    }
    return map;
  }, [ledger]);

  // Active panel items (filtered by search query)
  const activeTab   = STATUS_TABS.find((t) => t.id === active)!;
  const activeItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ledger
      .filter((e) => e.statusRaw === activeTab.statusRaw)
      .filter((e) =>
        !q ||
        e.name.toLowerCase().includes(q) ||
        (e.entity ?? "").toLowerCase().includes(q),
      )
      .sort((a, b) => Math.abs(b.points) - Math.abs(a.points));
  }, [ledger, activeTab, query]);

  const activeTotal = useMemo(
    () => activeItems.reduce((s, e) => s + e.points, 0),
    [activeItems],
  );

  return (
    <div className="space-y-5">
      {/* ─ KPI card ─────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border/70 bg-card px-4 py-4 shadow-sm">
        <p className={cn(USERS_HELPER_CLS, "tracking-wide")}>
          Points this period
        </p>

        <div className="mt-2.5 flex items-center gap-2.5 flex-wrap">
          <span className={cn(USERS_HERO_METRIC_CLS, "text-2xl text-foreground")}>
            {totalPts.toLocaleString()}
          </span>
          {boardAvgDelta !== null && (
            <span className={cn(
              "inline-flex items-center h-6 px-2 rounded text-sm font-medium ring-1 ring-inset",
              boardAvgDelta >= 0
                ? "bg-success/10 text-success ring-success/20"
                : "bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20",
            )}>
              {boardAvgDelta >= 0 ? "+" : ""}{boardAvgDelta} vs board
            </span>
          )}
        </div>

        {/* Settled progress bar */}
        <div className="mt-3 space-y-1 max-w-[240px]">
          <div className="flex items-center justify-between">
            <span className={USERS_HELPER_CLS}>Settled</span>
            <span className={cn(USERS_META_CLS, "text-muted-foreground")}>{settledPct}%</span>
          </div>
          <div className="h-1 rounded-full bg-muted/70 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${settledPct}%` }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="h-full rounded-full bg-primary"
            />
          </div>
        </div>

        {/* Earned / Pending stats */}
        <div className="mt-3 flex items-center gap-5">
          <span className="inline-flex items-baseline gap-1.5 text-sm font-normal">
            <span className="text-success font-medium tabular-nums">{formatPts(earnedPts)}</span>
            <span className={USERS_HELPER_CLS}>earned</span>
          </span>
          {pendingPts > 0 && (
            <span className="inline-flex items-baseline gap-1.5 text-sm font-normal">
              <span className="text-amber-500 dark:text-amber-400 font-medium tabular-nums">{formatPts(pendingPts)}</span>
              <span className={USERS_HELPER_CLS}>pending</span>
            </span>
          )}
        </div>
      </div>

      {/* ─ Tab nav + search + period select (single row) ───────────────── */}
      <div>
        <nav className="border-b border-border/70">
          <div className="flex items-center">
            {/* Tabs — left */}
            <div className="flex gap-5">
              {STATUS_TABS.map((tab) => {
                const on = tab.id === active;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => { setActive(tab.id); setQuery(""); }}
                    className={cn(
                      "inline-flex items-center gap-1.5 pb-2.5 pt-0.5 text-base transition-colors border-b-2 -mb-px whitespace-nowrap font-normal",
                      on
                        ? "text-foreground border-primary"
                        : "text-muted-foreground hover:text-foreground border-transparent",
                    )}
                  >
                    <i className={cn(tab.faIcon, "shrink-0 opacity-80 text-base")} aria-hidden />
                    {tab.label}
                    <span
                      className={cn(
                        "inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full tabular-nums text-sm font-medium leading-none",
                        on
                          ? "bg-muted text-foreground"
                          : "bg-muted/50 text-muted-foreground",
                      )}
                    >
                      {tabCounts[tab.id]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Search + period select — right */}
            <div className="ml-auto flex items-center gap-2 pb-2 pl-4 shrink-0">
              <div className={cn(
                "flex items-center gap-1.5 rounded-lg border px-2.5 py-1",
                "bg-muted/40 border-border/50",
                "transition-[border-color,box-shadow] duration-150 ease-out",
                "focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/35 focus-within:bg-card",
              )}>
                <Search className="size-3.5 shrink-0 text-muted-foreground/70" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="w-24 bg-transparent text-sm border-0 focus:outline-none text-foreground placeholder:text-muted-foreground/60"
                />
              </div>
              <UsersPeriodSelect
                value={period}
                onValueChange={setPeriod}
                options={POINTS_PERIOD_OPTIONS}
                triggerClassName="w-[8rem] shrink-0"
              />
            </div>
          </div>
        </nav>

        {/* ─ Panel ─────────────────────────────────────────────────────── */}
        <div className="pt-4">
          {activeItems.length === 0 ? (
            <TabEmpty
              icon={activeTab.faIcon}
              message={query
                ? `No ${activeTab.label.toLowerCase()} points match "${query}".`
                : `No ${activeTab.label.toLowerCase()} points.`}
            />
          ) : (
            <>
              <div className="space-y-2.5">
                {activeItems.map((entry, i) => (
                  <PointRow key={entry.id} entry={entry} index={i} />
                ))}
              </div>

              {/* Sticky footer — total for this tab */}
              <div className={cn(
                "sticky bottom-0 mt-3",
                "flex items-center justify-between gap-4",
                "border border-border/60 bg-card",
                "px-4 py-3 rounded-md",
              )}>
                <span className={USERS_HELPER_CLS}>
                  {activeItems.length} {activeTab.label.toLowerCase()} {activeItems.length === 1 ? "entry" : "entries"}
                </span>
                <span className={cn(
                  USERS_HERO_METRIC_CLS,
                  "text-base",
                  TAB_TOTAL_CLS[active] ?? "text-foreground",
                )}>
                  {formatPts(activeTotal)} pts
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
